import { useEffect, useState } from "react";
import { Check, TrendingUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { haptics } from "@/lib/interactions/haptics";

interface SuccessCheckmarkProps {
  onComplete?: () => void;
  size?: number;
  className?: string;
}

export const SuccessCheckmark = ({ 
  onComplete, 
  size = 48,
  className 
}: SuccessCheckmarkProps) => {
  useEffect(() => {
    haptics.success();
    const timer = setTimeout(() => {
      onComplete?.();
    }, 1000);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <div className={cn("animate-scale-bounce", className)}>
      <div 
        className="rounded-full bg-green-500 flex items-center justify-center"
        style={{ width: size, height: size }}
      >
        <Check className="text-white animate-draw-check" style={{ width: size * 0.5, height: size * 0.5 }} />
      </div>
    </div>
  );
};

interface PointsPopupProps {
  points: number;
  label?: string;
  position?: { x: number; y: number };
  onComplete?: () => void;
}

export const PointsPopup = ({ 
  points, 
  label = "points",
  position,
  onComplete 
}: PointsPopupProps) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    haptics.success();
    const timer = setTimeout(() => {
      setIsVisible(false);
      onComplete?.();
    }, 2000);
    return () => clearTimeout(timer);
  }, [onComplete]);

  if (!isVisible) return null;

  const style = position
    ? { position: 'fixed' as const, left: position.x, top: position.y }
    : {};

  return (
    <div
      className="pointer-events-none z-50 animate-float-up"
      style={style}
    >
      <div className="bg-primary text-primary-foreground px-4 py-2 rounded-full shadow-lg font-bold text-lg flex items-center gap-2">
        <TrendingUp className="w-5 h-5" />
        <span>+{points}</span>
        <span className="text-sm font-normal">{label}</span>
      </div>
    </div>
  );
};

interface StreakFireProps {
  count: number;
  className?: string;
}

export const StreakFire = ({ count, className }: StreakFireProps) => {
  return (
    <div className={cn("relative inline-flex items-center gap-2", className)}>
      <span className="text-2xl animate-flicker">🔥</span>
      <span className="text-xl font-bold text-orange-500 animate-scale-in">
        {count}
      </span>
      <span className="text-sm text-muted-foreground">day streak!</span>
    </div>
  );
};

interface ConfettiProps {
  trigger: boolean;
  onComplete?: () => void;
}

export const Confetti = ({ trigger, onComplete }: ConfettiProps) => {
  const [pieces, setPieces] = useState<Array<{ id: number; x: number; color: string; delay: number }>>([]);

  useEffect(() => {
    if (!trigger) return;

    haptics.success();

    // Generate confetti pieces
    const newPieces = Array.from({ length: 30 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      color: ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8'][Math.floor(Math.random() * 5)],
      delay: Math.random() * 200,
    }));

    setPieces(newPieces);

    const timer = setTimeout(() => {
      setPieces([]);
      onComplete?.();
    }, 3000);

    return () => clearTimeout(timer);
  }, [trigger, onComplete]);

  if (pieces.length === 0) return null;

  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {pieces.map((piece) => (
        <div
          key={piece.id}
          className="absolute top-0 w-2 h-2 animate-confetti"
          style={{
            left: `${piece.x}%`,
            backgroundColor: piece.color,
            animationDelay: `${piece.delay}ms`,
          }}
        />
      ))}
    </div>
  );
};
